import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ProposalService } from '../services/proposal.service';
import { ProjectService } from '../services/project.service';
import { ToastrService } from 'ngx-toastr';
import { ModalService } from '../services/modal.service';
import { getStoredUser } from '../helpers/user-storage.util';
import { RequirementsAcknowledgementComponent } from '../requirements-acknowledgement/requirements-acknowledgement.component';
import { projectHasSiteRequirements } from '../utils/site-requirements';
import { ModalComponent } from '../shared/modal/modal.component';
import { buildBulkProjectDetail } from '../list/bulk-eligibility.util';
import { ProjectActionLockService, lockIds } from '../services/project-action-lock.service';
import { BulkItemListComponent } from '../bulk-item-list/bulk-item-list.component';
@Component({
  selector: 'allbids-dialog',
  standalone: true,
  imports: [
    CommonModule,
    RequirementsAcknowledgementComponent,
    ModalComponent,
    BulkItemListComponent,
  ],
  templateUrl: './allbids-dialog.component.html',
  styleUrl: './allbids-dialog.component.scss',
})
export class AllbidsDialogComponent {
  @Input() active: boolean = false;
  @Output() modalClosed = new EventEmitter<boolean>();
  /**
   * The UNFILTERED project. Feeds `specialQuoteRequirements`,
   * `hasAnySiteRequirements` and the Project ID summary, so the
   * requirements-acknowledgement gate keeps its whole-project scope — a
   * special site condition on a site that is NOT part of this bulk submit
   * still has to be acknowledged. Narrowing this to the eligible set would
   * silently narrow that gate; the filtered set goes in `siteDetails` below.
   */
  @Input() data: any = '';
  @Input() documents: any = '';
  @Input() address: any = { streetAddress: '', cityStateZip: '' };
  @Input() numberOfBids: number = 0;
  /**
   * The products this submit actually writes, when the host is sweeping a
   * subset of the project (the bulk Submit All Quotes path) — also the rows
   * the itemized list renders. Left null by the existing single-submit
   * hosts (`info-header`, `project-overview-header`), which already pass a
   * one-site `data` and must keep posting it verbatim — hence the guard in
   * `writePayload` rather than an unconditional rebuild, and why the itemized
   * list only renders once this is supplied. Mirrors
   * `accept-offered-proposal-dialog.component.ts`'s `siteDetails` input.
   */
  @Input() siteDetails: any[] | null = null;
  isSubmitting = false;
  user = getStoredUser();

  get hasAnySiteRequirements(): boolean {
    return projectHasSiteRequirements(this.data);
  }

  constructor(
    private proposalService: ProposalService,
    private projectService: ProjectService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private lock: ProjectActionLockService
  ) {}

  /**
   * The ProjectDetail this submit posts: the full project with only the
   * products being submitted, when the host narrowed the set; otherwise
   * `data` verbatim, which is what the single-submit hosts pass and must
   * keep posting.
   */
  private get writePayload(): any {
    return this.siteDetails
      ? buildBulkProjectDetail(this.data, this.siteDetails)
      : this.data;
  }

  closeDialog() {
    this.active = false;
    this.modalClosed.emit(this.active);
  }

  toggleOpenSubmittedAllBidsModal() {
    this.modalService.toggleOpenSubmittedAllBidsModal();
  }

  submit() {
    this.isSubmitting = true;
    const payload = this.writePayload;
    this.lock
      .hold(lockIds(payload), this.proposalService.submitBid(payload, this.user.id))
      .subscribe({
        next: (response: any) => {
          this.isSubmitting = false;
          let apiResponse: any = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const data = apiResponse.data;
            if (data) {
              this.closeDialog();
              this.toggleOpenSubmittedAllBidsModal();
              this.projectService.updateProjectDataSubjectWithProjectDetail(data);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        }
      });
  }
}
